import type { PublicAuthor } from '@dove/shared';
import { faceUrlFor, initials, monogramStyle } from '@/lib/avatar';
import { cn } from '@/lib/utils';

/**
 * The stamp in the corner of every letter.
 *
 * WHY A LETTER NEEDS ONE AT ALL. The sheet was already warm paper with a serif
 * body, and it still read as a card with a paragraph on it, because every other
 * card in the app is also a rounded field with a paragraph on it. Paper is a
 * texture; a stamp is a piece of evidence that something travelled. It is the
 * cheapest mark that makes the metaphor land, and it lands in the one corner of
 * the sheet the text never reaches.
 *
 * THAT ARGUMENT GOT STRONGER, NOT WEAKER, when the paper went (docs/5 § D260803-0020). A
 * sheet is now literally the app's card, so the texture is no longer even
 * pretending to carry the claim, and postage in the corner is most of what is
 * left saying an object went somewhere. Nothing in this file changed; what
 * changed is how much of the work it is doing.
 *
 * IT CARRIES THE SENDER'S PORTRAIT, AND THAT IS A REVERSAL (docs/5 § D260802-0014). It
 * used to carry their pigeon, on the reasoning that a person's bird is shown
 * beside their name on every letter (docs/1 § Your pigeon) and the corner of the
 * sheet was where a letter kept it. The rule was being kept and the object was
 * not: a bird in a brass rectangle is a logo in a box, and next to a photograph
 * of the same person in the letterhead it read as the decorative half of a pair.
 * A stamp is a PORTRAIT IN AN ENGRAVED FRAME — that is what postage has looked
 * like since the Penny Black — so the photograph moved into the frame and the
 * bird moved to where docs/1 actually puts it, beside the name. Each is now
 * doing the job the other was borrowing: one person, drawn once, and one stamp
 * that looks like a stamp.
 *
 * SQUARE, AND NOT A ROUNDED AVATAR MOVED ACROSS THE SHEET. There used to be a
 * `Face` — a circle with a hairline ring and a rounded monogram — and every one of
 * those undoes the frame, so this drew the photograph from the `lib/avatar` helpers
 * itself and only the crop differed.
 *
 * THERE IS NO CIRCLE LEFT TO DIVERGE FROM (docs/5 § D260805-1523). `Face` and
 * `PosterAvatar` are gone: a person is drawn as their bird and their postage on every
 * surface in the product, so this file is now the ONLY rendering of somebody's
 * photograph and the one place the crop is decided. What that costs is stated here
 * rather than discovered: this is worn from 26px in a feed byline to 160px in the
 * camera, so nothing in the drawing may be a fixed pixel value (see the geometry
 * below), and every caller sitting on app chrome rather than on a sheet has to
 * declare its surface (see `--sheet-paper`).
 *
 * THE PERFORATION IS DRAWN, NOT MASKED. The scallops are circles filled in the
 * sheet's own `--paper`, painted over the stamp's edges. A CSS `mask` with four
 * repeating radial gradients is the fashionable way to do this and it fails in
 * two places that matter: `mask-composite` support is uneven in older WebViews,
 * which is what Capacitor ships, and a masked element gets its own compositing
 * layer — the same trap that put a visible seam across the paper when a previous
 * letter set `opacity-95` on it. Circles in the background colour composite
 * nothing and work everywhere.
 *
 * They do assume the stamp sits on A DECLARED SURFACE, and that assumption is
 * load-bearing at every call site. Sheets declare one for free (index.css sets
 * `--sheet-paper` on both `letter-sheet` and `letter-note`): a letter sheet, an
 * envelope's flap, a feed post. **Everything else is app chrome and has to say so
 * itself** — a profile's header, a row in the Loft's list, a notification, a
 * follower, a composer's letterhead, an edit screen — all setting
 * `--sheet-paper: var(--background)` in a `style` prop, which is why `PAPER_IS_CHROME`
 * below exists rather than that declaration being spelled out a dozen times. Get it
 * wrong and the
 * scallops are punched in the wrong colour: since docs/5 § D260803-0020 a sheet's surface is
 * the app's CARD, so the failure on chrome is card-coloured dots on the page ground
 * rather than the cream-on-grey it used to be — a smaller error than before, and
 * still visible in the dark, where `--card` sits a clear step above
 * `--background`.
 *
 * NO DENOMINATION, NO COUNTRY, NO CITY ON THE STAMP FACE. At this size a word is
 * a grey smudge that costs contrast and says nothing, and the postmark already
 * has a home in the letterhead, where it is legible and where the city-level rule
 * that governs it (docs/8 § E) is visible next to the other metadata rather than
 * hidden in an ornament.
 */

/**
 * Stamp geometry, in the drawing's own units. Taller than wide, as postage is.
 *
 * THE DRAWING IS FIXED AND THE BOX IS SCALED, which is the cheap way to have one
 * stamp at two sizes. A letter draws one at 40px and a feed post at 26px (see
 * `PostCard`, which argues why a post is stamped at all); the `viewBox` stays
 * `0 0 40 48` either way and the browser scales it, so every coordinate below is a
 * plain number rather than a fraction multiplied back up at a dozen call sites.
 * The frame, the scallops and the postmark shrink together for free, which is the
 * property that matters: the small one is a small stamp, not a stamp with a heavy
 * border. `vector-effect: non-scaling-stroke` is what holds the two hairlines at a
 * hairline once the drawing is scaled down.
 *
 * The perforation COUNTS are fixed for a different reason. A stamp has the
 * scallops it has; deriving the count from the width would give a 26px stamp three
 * teeth a side and a 40px stamp five, and then the two really would be different
 * objects.
 */
const W = 40;
const H = 48;
/** The printed area's margin: the perforated border a stamp is trimmed to. */
const MARGIN = 4;
/** Perforation radius, and how many scallops fit each edge. */
const PERF_R = 2.1;
const PERF_X = 5;
const PERF_Y = 6;
/** The engraved rule sits this far outside the printed area. */
const RULE = 1.25;

/* Evenly spaced along each edge, centred ON it, so half of every circle falls
   outside the stamp and bites a scallop out of the border. Module scope, because
   the drawing does not depend on the rendered size. */
const ACROSS = Array.from({ length: PERF_X }, (_, i) => (W * (i + 0.5)) / PERF_X);
const DOWN = Array.from({ length: PERF_Y }, (_, i) => (H * (i + 0.5)) / PERF_Y);

/**
 * The parts of the drawing that depend on nothing.
 *
 * Built once as elements rather than rebuilt per render. This matters now that a
 * stamp is on every post in a scrolling feed: the perforation alone is 22 circles,
 * and as a constant element React short-circuits the whole subtree on referential
 * equality instead of diffing them under every post on every parent render.
 */
const PRINTED_AREA = (
  <>
    {/* A brass veil over the photograph, holding it to the sheet's own palette so
        a full-colour snapshot in the corner of a letter reads as printed rather
        than as a second avatar. Low enough to leave a face a face — this is a
        tint, not a duotone, and it is drawn as a rect in the SVG rather than as a
        CSS filter or a blend mode, neither of which composites reliably in the
        WebView Capacitor ships. */}
    <rect
      x={MARGIN}
      y={MARGIN}
      width={W - MARGIN * 2}
      height={H - MARGIN * 2}
      fill="var(--brass)"
      opacity="0.2"
    />
    {/* The engraved rule around the printed area. */}
    <rect
      x={MARGIN - RULE}
      y={MARGIN - RULE}
      width={W - (MARGIN - RULE) * 2}
      height={H - (MARGIN - RULE) * 2}
      fill="none"
      stroke="var(--brass)"
      strokeOpacity="0.45"
      strokeWidth="0.75"
      vectorEffect="non-scaling-stroke"
    />
  </>
);

/* The perforation. A colour rather than `transparent`: these punch the stamp
   against the sheet it is lying on, and the sheet is paper.

   `--sheet-paper` rather than `--paper`, because there are two stocks — the one
   you receive on and the one you send on — and a stamp reads whichever it was
   stuck to. The fallback covers a caller drawing a stamp on something that is not
   a `letter-sheet` or a `letter-note`. */
const PERFORATION = (
  <g fill="var(--sheet-paper, var(--paper))">
    {ACROSS.map((x) => (
      <circle key={`t${x}`} cx={x} cy={0} r={PERF_R} />
    ))}
    {ACROSS.map((x) => (
      <circle key={`b${x}`} cx={x} cy={H} r={PERF_R} />
    ))}
    {DOWN.map((y) => (
      <circle key={`l${y}`} cx={0} cy={y} r={PERF_R} />
    ))}
    {DOWN.map((y) => (
      <circle key={`r${y}`} cx={W} cy={y} r={PERF_R} />
    ))}
  </g>
);

/* TWO THIN RINGS AND NOTHING ELSE. The first version added the three wavy bars a
   real cancellation carries, and at this size they closed the gap between the
   rings into a solid blob — the whole mark read as a loop of wire dropped on the
   stamp rather than as ink. A postmark is recognisable from its concentric rings
   alone, which is the part that survives being small. Tucked into the top corner
   and clipped by the perforations drawn after it, so it reads as applied rather
   than centred like a logo.

   In `paper-ink` rather than brass now that there is a photograph underneath:
   postmark ink is dark, and a brass ring over a face was invisible on half the
   portraits. */
const POSTMARK = (
  <g
    stroke="var(--paper-ink)"
    strokeOpacity="0.42"
    strokeWidth="0.8"
    fill="none"
    vectorEffect="non-scaling-stroke"
  >
    <circle cx={W * 0.74} cy={H * 0.19} r="6" />
    <circle cx={W * 0.74} cy={H * 0.19} r="8.4" />
  </g>
);

/**
 * What a stamp lying on APP CHROME has to declare, in one place.
 *
 * The perforations are punched in `--sheet-paper` and fall back to a sheet's own
 * stock, so a stamp on the page's ground scallops in card colour unless it says
 * otherwise — the failure the note above calls the tell. It was two call sites
 * spelling the same object literal out by hand and is now a dozen, which is one
 * typo away from a wrong-coloured stamp on a screen nobody re-checked.
 *
 * Spread into `style` rather than wrapped in a component: a caller may also need
 * geometry of its own, and `style` merges.
 */
export const PAPER_IS_CHROME = {
  '--sheet-paper': 'var(--background)',
} as React.CSSProperties;

export function LetterStamp({
  author,
  portrait,
  cancelled = false,
  width = 40,
  className,
  style,
  ...props
}: React.ComponentProps<'span'> & {
  /**
   * The sender. See above: the portrait on the stamp is theirs.
   *
   * OPTIONAL ONLY BECAUSE OF `portrait`. Every stamp of a person passes one — it is
   * where the photograph and the monogram come from — and the one caller that does
   * not is the camera, which has a live preview to put in the frame and, at
   * onboarding, no account to name yet.
   */
  author?: PublicAuthor;
  /**
   * Something else in the printed area, instead of the author's photograph.
   *
   * THE FRAME IS THE PRODUCT'S ONE PORTRAIT CROP, and this is what stops a second
   * one being invented (docs/5 § D260805-1523). `CaptureAvatar` frames a face for a
   * photograph that will be WORN here, so it has to compose against these
   * proportions — a round viewfinder over a square upload cropped to a stamp was
   * three different pictures. Passing the live `<video>` in makes the thing being
   * framed and the thing being worn the same object, and it costs this file one
   * slot rather than the geometry being re-derived somewhere else.
   *
   * It fills the printed area: give it `size-full object-cover`.
   */
  portrait?: React.ReactNode;
  /**
   * Franked, because it travelled.
   *
   * Set on letters that ARRIVED and never on your own. It is a fact about the
   * post rather than about the person — a cancellation says the sorting office
   * handled it, which is exactly the sort of thing a letter surface is allowed to
   * report, unlike anything describing what the other person did or when.
   */
  cancelled?: boolean;
  /**
   * How wide to draw it, in px. The height follows from postage's own proportion.
   *
   * 40 is a letter's stamp and the default, so the surface that had the only call
   * site for a year keeps its geometry untouched. A post passes 26 — see
   * `PostCard`, and the note above on why one set of ratios serves both.
   */
  width?: number;
}) {
  const face = author && faceUrlFor(author);

  return (
    <span
      aria-hidden
      /* Decorative in the accessibility tree. The sheet's own letterhead already
         names the sender and the date, which is everything the stamp depicts, and
         a screen reader announcing "stamp" on every letter is noise.

         Rotated a couple of degrees because a stamp applied by hand is never
         square to the page, and that tiny imperfection is most of what separates
         this from a logo in a box. Not more than that: past about 3° it reads as
         a mistake rather than as a hand. */
      className={cn(
        /* `inline-block` IS LOAD-BEARING, and its absence was invisible for as
           long as this had one call site. A `span` is `display: inline`, and an
           inline box ignores `width` and `height` — the stamp only ever had a size
           because `LetterSheet` puts it in a flex row, and a flex item is
           blockified. Dropped into a `<a>` on a feed post it collapsed to a 0 × 0
           box that rendered nothing. Stated here rather than fixed at the call
           site, because "has a size" is a property of a stamp, not of where it is
           hung. In a flex or grid parent this still blockifies exactly as before.

           `letter-stamp` is the trimmed border — a wash of `brass` mixed toward
           the stock it lies on. A class rather than an inline `color-mix`, because
           the value is constant per stock and this now renders once per post in a
           scrolling feed; index.css resolves it once. */
        'letter-stamp relative inline-block shrink-0 -rotate-2 select-none rtl:rotate-2',
        className,
      )}
      /*
         `style` IS MERGED, AND `...props` NO LONGER CARRIES IT — the same trap
         `PosterAvatar` records, hit the same way. A caller passing `style` used to
         replace this object wholesale and take the geometry with it: the stamp
         became a 0 × 0 inline-block whose children are all absolutely positioned,
         so it vanished completely rather than rendering wrong. The first caller to
         pass one was the profile, setting `--sheet-paper` (see `ProfileIdentity`),
         which is exactly the kind of thing a caller has to be able to set.
      */
      style={{ width, height: width * (H / W), ...style }}
      {...props}
    >
      {/* The printed area. Square, `object-cover`, and inset by the margin the
          perforations are punched through — as a percentage of each axis, so it
          tracks the box without needing the rendered size in JS. */}
      <span
        className="absolute overflow-hidden"
        style={{
          insetInline: `${(MARGIN / W) * 100}%`,
          insetBlock: `${(MARGIN / H) * 100}%`,
        }}
      >
        {portrait ? (
          /* Somebody else's picture in the frame — today the camera's live preview.
             See `portrait`: it is checked FIRST, so a caller with both an author and
             something to show gets the thing it passed rather than the stored
             photograph it is about to replace. */
          portrait
        ) : face ? (
          <img
            src={face}
            alt=""
            loading="lazy"
            decoding="async"
            className="size-full object-cover"
          />
        ) : author ? (
          /* The same monogram every other surface draws, squared off — and no
             longer the rare branch: it is what postage from anybody who has not
             taken a photograph looks like (docs/5 § D260805-0422). Sized off the stamp
             rather than fixed at 13px: two initials in a 13px face do not fit
             inside a 26px stamp's printed area. */
          <span
            className="monogram flex size-full items-center justify-center font-medium"
            style={{ ...monogramStyle(author.handle), fontSize: width * 0.325 }}
          >
            {initials(author.name)}
          </span>
        ) : null}
      </span>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="absolute inset-0 size-full"
        /* The cancellation arcs overhang the stamp, as a real postmark does. */
        style={{ overflow: 'visible' }}
      >
        {PRINTED_AREA}
        {/* Drawn before the perforation, so the scallops clip it and the mark
            reads as applied rather than centred like a logo. */}
        {cancelled && POSTMARK}
        {PERFORATION}
      </svg>
    </span>
  );
}
